<script setup lang="ts">
import { useHead, useRuntimeConfig } from '#app'
import { useI18n, useLocalePath } from '#i18n'
import { useCountryConfig } from '~/composables/useCountryConfig'

const props = defineProps<{
  error: any
}>()

const { t } = useI18n()
const { titleTranslationKey } = useCountryConfig()
const isProd = useRuntimeConfig().public.environment === 'prod'
const localePath = useLocalePath()
const { $log } = useNuxtApp()

useHead(
  { title: t(titleTranslationKey, { title: t('errors.page.notFound.title') }) },
)

$log('warn', {
  title: 'User landed on error page',
  message: props.error.message ?? JSON.stringify(props.error),
})
</script>

<template>
  <v-container class="pa-6 mb-16" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" md="1">
        <NuxtImg src="/images/bird.svg" alt="Error" width="120" />
      </v-col>
      <v-col cols="12" md="6" align-self="center">
        <div>
          <p class="font-weight-bold text-h4">
            {{ $t('errors.page.unknown.title') }}
          </p>
        </div>
        <div>
          <p class="text-h5">
            {{ $t('errors.page.unknown.subtitle') }}
          </p>
          <div v-if="!isProd">
            <pre v-if="!error?.stack" class="text-body-1 text-pre-wrap" v-text="error" />
            <div v-else class="text-body-1 text-pre-wrap">
              <dl class="pre-value">
                <template v-for="(value, name) in error" :key="name">
                  <dt v-text="name" />
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <dd v-html="value.toString().replace(/\n(at .*)/g, '<span>$1</span>')" />
                </template>
              </dl>
            </div>
          </div>
          <v-btn-primary class="mt-3" :to="localePath('/')">
            {{ $t('errors.page.backToHome') }}
          </v-btn-primary>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped lang="scss">
dl {
  font-family: monospace;
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: .4em;
  dt {
    text-align: end;
    &::after {
      content: ':';
    }
  }
  dd {
    white-space: pre-wrap !important;
    :deep(span) {
      padding-left: 25px;
      text-indent: -25px;
      display: block;
    }
  }
}
</style>
